import { Link, useLocation } from "react-router-dom";

import { ChevronDownIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { NavItem, NavSection } from "@/constants/appSidebar";
import { useNavSections } from "@/hooks/useNavSections";
import { isPathActive, isSectionActive } from "@/lib/navigation";
import { useActiveTeam } from "@/store/useTeamStore";

import HeaderUser from "./HeaderUser";

// 현재 위치한 항목은 메뉴 안에서도 알아볼 수 있게 표시한다.
const CURRENT_ITEM_CLASS =
  "aria-[current=page]:bg-accent aria-[current=page]:font-medium";

function NavMenuItem({ item }: { item: NavItem }) {
  const { pathname } = useLocation();

  if (!item.items?.length) {
    return (
      <DropdownMenuItem
        render={<Link to={item.url} />}
        aria-current={isPathActive(pathname, item.url) ? "page" : undefined}
        className={CURRENT_ITEM_CLASS}
      >
        <item.icon />
        {item.title}
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <item.icon />
        {item.title}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {item.items.map((subItem) => (
          <DropdownMenuItem
            key={subItem.url}
            render={<Link to={subItem.url} />}
            aria-current={pathname === subItem.url ? "page" : undefined}
            className={CURRENT_ITEM_CLASS}
          >
            {subItem.title}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

// 업무 흐름 묶음 하나 = 드롭다운 하나. 마우스를 올리거나 눌러서 열 수 있다.
function NavDropdown({ section }: { section: NavSection }) {
  const { pathname } = useLocation();
  const active = isSectionActive(section, pathname);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        openOnHover
        delay={100}
        data-active={active || undefined}
        className="group inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap text-foreground/70 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-active:text-foreground data-popup-open:text-foreground"
      >
        {section.label}
        <ChevronDownIcon className="size-4 transition-transform duration-150 group-data-popup-open:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-auto min-w-48">
        {section.items.map((item) => (
          <NavMenuItem key={item.url} item={item} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 로그인 후 화면 공용 상단 헤더. 활성 팀의 로고·이름·한 줄 설명과 그 옆의 업무 흐름
 * 묶음(`NAV_SECTIONS`)별 드롭다운 메뉴를 한 덩어리로 묶어 **가운데**에 놓는다 —
 * 바탕과 아래 테두리는 화면 폭 전체로 깔린다. 폭이 모자라면 덩어리가 화면 폭까지만
 * 줄어들고 메뉴 줄이 가로로 스크롤된다. 덩어리의 오른쪽 끝에는 로그인 사용자의
 * 아이디와 로그아웃 버튼(`HeaderUser`)이 붙는다.
 * 메뉴는 사이드바와 같은 `useNavSections` 에서 오므로 팀별 구성과 관리자 전용
 * 항목 숨김이 동일하게 적용된다.
 */
export default function AppHeader() {
  const team = useActiveTeam();
  const sections = useNavSections();
  // 로고를 누르면 메뉴의 첫 화면(홈)으로 — `/` 는 공개 랜딩 페이지라 쓰지 않는다.
  const homeUrl = sections[0]?.items[0]?.url ?? "/home";
  const Logo = team.logo;

  return (
    <header className="sticky top-0 z-40 border-b bg-background">
      <div className="mx-auto flex h-14 w-fit max-w-full items-center gap-8 px-4 sm:px-6">
        <Link to={homeUrl} className="flex shrink-0 items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-lg bg-neutral-800 text-white">
            <Logo className="size-5" />
          </span>
          <span className="flex items-baseline gap-3">
            <span className="text-xl font-bold tracking-tight">
              {team.name}
            </span>
            <span className="hidden text-xs text-muted-foreground md:inline">
              {team.plan}
            </span>
          </span>
        </Link>
        <nav
          aria-label="주 메뉴"
          className="flex min-w-0 items-center gap-1 overflow-x-auto py-1"
        >
          {sections.map((section) => (
            <NavDropdown key={section.id} section={section} />
          ))}
        </nav>
        <HeaderUser />
      </div>
    </header>
  );
}
